import { StandardEvents } from '@shopify/events';
import { morphSection, sectionRenderer } from '@theme/section-renderer';

/**
 * Blush: Shipping Protection toggle for the cart drawer.
 *
 * OPT-IN: the protection is never added automatically. It is only added when the customer switches it on.
 *
 * Safety rules this file is built around:
 * - It only ever sets the quantity of the protection variant, through /cart/update.js with
 *   `updates: { <variant id>: <quantity> }`. That sets an exact quantity, so repeating it can never
 *   stack up, and it cannot change any other line in the cart.
 * - It never touches a product's add-to-cart request. The theme adds products exactly as it always did.
 * - All changes run one at a time (queue), and each one re-reads the cart before acting.
 *
 * Behaviour:
 * - The switch sets the protection to 1 or 0. It starts switched off.
 * - Nothing here adds the protection on its own. If a protection line already exists it is kept at exactly 1.
 * - When no products are left in the cart, a lone protection line is removed.
 *
 * Keep the variant id in sync with snippets/cart-drawer.liquid, snippets/cart-bubble.liquid,
 * snippets/blush-shipping-protection.liquid and assets/cart-icon.js.
 */
const VARIANT_ID = 48281865978054;
// Older carts may still carry this attribute from when the protection was switched on by default; it is cleared.
const CHOICE_ATTRIBUTE = 'blush_shipping_protection';

// The real fetch. Every request this file makes uses it.
const nativeFetch = window.fetch.bind(window);

/** @param {any} item */
const isProtection = (item) => Number(item?.variant_id ?? item?.id) === VARIANT_ID;

/**
 * @param {string} url
 * @param {object} body
 * @returns {Promise<any>}
 */
const postJson = (url, body) =>
  nativeFetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  }).then((response) => response.json());

/** @returns {Promise<any>} */
const readCart = () =>
  nativeFetch(`${Theme.routes.cart_url}.json`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  }).then((response) => response.json());

/** One change at a time. @type {Promise<unknown>} */
let queue = Promise.resolve();

/**
 * @template T
 * @param {() => Promise<T>} task
 * @returns {Promise<T>}
 */
function enqueue(task) {
  const run = queue.then(task, task);
  queue = run.catch(() => {});
  return run;
}

/** @param {boolean} busy */
function setBusy(busy) {
  for (const element of document.querySelectorAll('blush-shipping-protection')) {
    element.toggleAttribute('data-busy', busy);
    const input = element.querySelector('input[type="checkbox"]');
    if (input instanceof HTMLInputElement) input.disabled = busy;
  }
}

/** @param {boolean} checked */
function setChecked(checked) {
  for (const input of document.querySelectorAll('blush-shipping-protection input[type="checkbox"]')) {
    if (input instanceof HTMLInputElement) input.checked = checked;
  }
}

/** @returns {{ id: string, mode: 'hydration' | 'full' }[]} The cart sections on this page. */
function getCartSections() {
  /** @type {{ id: string, mode: 'hydration' | 'full' }[]} */
  const sections = [];
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) {
      sections.push({ id: element.dataset.sectionId, mode: element.hasAttribute('data-drawer') ? 'hydration' : 'full' });
    }
  }
  return sections;
}

/**
 * Sets the protection to an exact quantity (and/or the cart attributes), then re-renders the cart sections.
 * @param {number | null} quantity - 1 or 0, or null to leave the quantity alone.
 * @param {{ attributes?: Record<string, string>, render?: boolean }} [options]
 */
async function setProtectionQuantity(quantity, { attributes, render = false } = {}) {
  const cartSections = render ? getCartSections() : [];
  /** @type {Record<string, any>} */
  const body = {};
  if (quantity !== null) body.updates = { [VARIANT_ID]: quantity };
  if (attributes) body.attributes = attributes;
  if (cartSections.length > 0) {
    body.sections = cartSections.map((section) => section.id).join(',');
    body.sections_url = window.location.pathname;
  }

  const response = await postJson(`${Theme.routes.cart_url}/update.js`, body);
  if (response.status || response.errors || !Array.isArray(response.items)) {
    throw new Error(response.description || response.message || 'Could not update shipping protection');
  }

  for (const { id, mode } of cartSections) {
    const html = response.sections?.[id];
    if (!html) continue;
    sectionRenderer.abortRender(id);
    await morphSection(id, html, { mode });
  }

  setChecked(response.items.some(isProtection));
  return response;
}

/**
 * If a protection line already exists, keep it at exactly one. Never adds the protection.
 * @param {{ render: boolean }} options
 */
function keepProtectionAtOne({ render }) {
  return enqueue(async () => {
    const cart = await readCart();
    const existing = (cart.items ?? []).find(isProtection);
    if (existing && existing.quantity !== 1) await setProtectionQuantity(1, { render });
  });
}

/**
 * The customer used the switch.
 * @param {boolean} on
 */
function onCustomerToggle(on) {
  return enqueue(async () => {
    setBusy(true);
    try {
      await setProtectionQuantity(on ? 1 : 0, { render: true });
    } catch (error) {
      console.warn('[shipping-protection] Could not update shipping protection:', error);
      setChecked(!on);
    } finally {
      setBusy(false);
    }
  });
}

/**
 * No products are left: remove a lone protection line and clear any old choice attribute from earlier carts.
 */
function cleanUpWithoutProducts() {
  return enqueue(async () => {
    const cart = await readCart();
    const items = cart.items ?? [];
    if (items.some((item) => !isProtection(item))) return;

    const hasProtection = items.some(isProtection);
    const hasChoice = cart.attributes?.[CHOICE_ATTRIBUTE] !== undefined;
    if (!hasProtection && !hasChoice) return;

    await setProtectionQuantity(hasProtection ? 0 : null, {
      render: hasProtection,
      attributes: hasChoice ? { [CHOICE_ATTRIBUTE]: '' } : undefined,
    });
  }).catch((error) => console.warn('[shipping-protection] Could not clean up the cart:', error));
}

class BlushShippingProtection extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);

    // Only the protection line is left in the cart (the last product was removed), so take it out too.
    if (this.hasAttribute('data-orphan')) cleanUpWithoutProducts();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLInputElement) onCustomerToggle(input.checked);
  };
}

if (!customElements.get('blush-shipping-protection')) {
  customElements.define('blush-shipping-protection', BlushShippingProtection);
}

// After any cart change: keep the switch in step with the cart and clean up an empty cart.
// This never adds the protection; it only reflects what the customer chose.
document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  const update = /** @type {any} */ (event);

  update.promise
    ?.then((/** @type {any} */ { detail }) => {
      const items = detail?.items;
      if (!Array.isArray(items)) return;

      const protection = items.find(isProtection);
      requestAnimationFrame(() => setChecked(Boolean(protection)));

      if (!items.some((item) => !isProtection(item))) {
        cleanUpWithoutProducts();
        return;
      }

      if (protection && protection.quantity !== 1) keepProtectionAtOne({ render: true }).catch(() => {});
    })
    .catch(() => {});
});
